import { FiLinkToType } from '@fi-shared/fi-text-item-helper/fi-link-to-type';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TextEditorInsertLinkService } from '@fi-shared/fi-text-editor/fi-text-editor-insert-link/text-editor-insert-link.service';
import { TextEditorInsertEmbedVideoService } from '@fi-shared/fi-text-editor/fi-text-editor-insert-embed-video/text-editor-insert-embed-video.service';
import { ITextEditorLinkAttachment, ITextEditorActiveButtons } from '@fi-shared/fi-text-editor/fi-text-editor';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { blurActiveElement, TextEditorService } from '@fi-shared/fi-text-editor/text-editor.service';
import { ITextPanelMenuItem, TextPanelBase } from '@fi-shared/fi-text-panel/simple-text-panel/text-panel-base';
import { fiDebounce, findClosest } from '@fi-shared/utils';
import { TextEditorAttachmentType } from '@fi-shared/fi-text-editor/fi-text-editor-attachment/text-editor-attachment-type';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiInsertMedia2Service } from '@fi-shared/fi-insert-media/fi-insert-media2.service';
import { ObjectTypes } from '@fi-shared/types';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { FiReplaceLinkTypes } from '@fi-shared/fi-directives/replace-with-links/replace-with-links.directive';
import { LinkToTypesService } from '@fi-shared/link-to/link-to-types.service';
import { ILightWeightUser } from '@fi-shared/models';
import { TextEditorTagUsersService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-tag-users.service';

@Component({
    selector: 'ccm-text-editor',
    templateUrl: './text-editor.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./text-editor.component.scss'],
    host: { class: 'ccm-text-editor' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: TextEditorComponent,
            multi: true,
        },
    ],
    standalone: false,
})
export class TextEditorComponent extends TextPanelBase implements OnInit, ControlValueAccessor, OnDestroy {
    @Input() ngModel: string;
    @Input() moduleType;
    @Input() searchObjectId: number;
    @Input() saveDisabled: boolean;
    @Input() placeholder: string;
    @Input() autofocus: boolean = false;
    @Input() attachmentsCount: number;
    @Input() objectType: ObjectTypes;
    @Input() disableFormattingOnPaste: boolean;
    @Input() taggedUsers: ILightWeightUser[] = [];
    @Input() replaceLinkTypes = [FiReplaceLinkTypes.Url, FiReplaceLinkTypes.Email];

    @Output() ngModelChange = new EventEmitter<string>();
    @Output() onSave = new EventEmitter<void>();
    @Output() onCancel = new EventEmitter<void>();
    @Output() editorOnFocus = new EventEmitter<void>();
    @Output() onNewAttachment = new EventEmitter<IMediaBankFileReference>();
    @Output() onUpdateLinkAttachment = new EventEmitter<ITextEditorLinkAttachment>();
    @Output() containerHeightChanged = new EventEmitter();

    @ViewChild('editor', { static: true }) editorElement: ElementRef;

    controlValueAccessorOnChange: (v) => void;
    textModel: string;
    fontStyleButtons: string[];
    editorElementCurrentHeight: number;
    protected availableLinkToItems: ITextPanelMenuItem[];
    fiLinkToType = FiLinkToType;
    focusedNodeElement: HTMLElement;
    isGiphyOverlayShown$ = new BehaviorSubject<boolean>(false);
    textEditorOpenedDialogs: ITextEditorActiveButtons = {};
    FiReplaceLinkTypes = FiReplaceLinkTypes;

    constructor(
        public elementRef: ElementRef<HTMLElement>,
        private textEditorService: TextEditorService,
        private textEditorTagUsersService: TextEditorTagUsersService,
        private textEditorInsertLinkService: TextEditorInsertLinkService,
        private textEditorInsertEmbedVideoService: TextEditorInsertEmbedVideoService,
        private linkToTypesService: LinkToTypesService,
        private fiDialogsService: FiDialogsService,
        private fileUploadService: FiFileUpload2Service,
        private fiInsertMedia2Service: FiInsertMedia2Service,
        injector: Injector,
    ) {
        super(injector, elementRef);
        this.fontStyleButtons = ['bold', 'underline', 'italic'];
    }

    writeValue(obj: any): void {}

    registerOnChange(fn: any): void {
        this.controlValueAccessorOnChange = fn;
    }

    registerOnTouched(fn: any): void {}

    setDisabledState(isDisabled: boolean): void {}

    ngOnInit() {
        this.textModel = this.ngModel;
        this.editorElementCurrentHeight = this.editorElement.nativeElement.offsetHeight;
        this.availableLinkToItems = this.linkToTypesService.getAvailableLinkToItems();
        this.initializeUserTags();

        fromEvent(this.elementRef.nativeElement, 'keydown')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(($event: KeyboardEvent) => {
                this.textEditorService.handleUserTagBackspace($event, this.editorElement.nativeElement, () =>
                    this.triggerChangeEvent(),
                );
                this.textEditorService.handleTabKeyPress($event);
            });
    }

    private initializeUserTags(): void {
        const content = this.textEditorTagUsersService.getConvertedFromViewToEditModeUserTags(
            this.ngModel,
            this.taggedUsers,
        );
        this.ngModel = content;
        this.textModel = content;
        this.ignoreNextUserTagsInitChange = true;
        this.cdr.detectChanges();
        this.textEditorTagUsersService.applyUserTagBehavior(this.editorElement.nativeElement, () =>
            this.triggerChangeEvent(),
        );
        this.ignoreNextUserTagsInitChange = false;
    }

    Focus() {
        this.editorOnFocus.emit();
        // scroll up only in not iphone mobile devices
        if (
            !/Version\/[\d\.]+.*Safari/.test(navigator.userAgent) &&
            window.matchMedia &&
            window.matchMedia('(max-width: 768px)').matches
        ) {
            setTimeout(() => {
                this.elementRef.nativeElement.scrollIntoView(true);
            }, 1000);
        }
        this.setFocusedElement();
    }

    private isContentChanged: boolean = false;
    private ignoreNextUserTagsInitChange: boolean = false;
    ContentChange($event) {
        if (this.ignoreNextUserTagsInitChange && $event === this.textModel) {
            this.ignoreNextUserTagsInitChange = false;
            return;
        }

        this.isContentChanged = true;
        this.ngModel = $event;
        this.doPropagateChanges();
        //this.doPropagateChangesDebounced(); // costly operation, should perform deferred

        let editorElementHeight = this.editorElement.nativeElement.offsetHeight;
        if (this.isEditorHeightChanged(editorElementHeight)) {
            this.editorElementCurrentHeight = editorElementHeight;
            this.containerHeightChanged.emit();
        }
        this.setFocusedElement();
    }

    isEditorHeightChanged(containerHeight: number) {
        return this.editorElementCurrentHeight !== containerHeight;
    }

    insertLink(linkToType: any) {
        blurActiveElement();
        this.textEditorOpenedDialogs.link = true;
        this.textEditorInsertLinkService
            .insertLink({
                parentElement: this.elementRef.nativeElement,
                linkToType,
            })
            .afterClosed(() => (this.textEditorOpenedDialogs.link = false));
    }

    insertMedia() {
        blurActiveElement();
        this.textEditorOpenedDialogs.attachments = true;
        this.fiInsertMedia2Service
            .openInsertMediaDialogWithFileSizes(this.objectType)
            .onYesButtonClick((result) => {
                result.mediaBankFileReferences.forEach((mediaBankReference) => {
                    this.onNewAttachment.emit(mediaBankReference);
                });
                this.getEditor().focus();
            })
            .afterClosed(() => (this.textEditorOpenedDialogs.attachments = false));
    }

    insertTag() {
        document.execCommand('insertText', false, '@');
    }

    insertEmbedVideo(attachment?: ITextEditorLinkAttachment) {
        const contentEditableElem = this.getEditor();
        this.textEditorOpenedDialogs.embed = true;

        this.textEditorInsertEmbedVideoService.openInsertEmbedVideoDialog(attachment, {
            onYesButtonClick: (attachment) => {
                this.onUpdateLinkAttachment.emit(attachment);
                setTimeout(() => this.updateTextModel());
            },
            onNoButtonClick: () => {
                contentEditableElem.focus();
            },
            afterClosed: () => {
                this.textEditorOpenedDialogs.embed = false;
            },
        });
    }

    toggleGiphyOverlay(isShown: boolean) {
        setTimeout(() => this.isGiphyOverlayShown$.next(isShown));
    }

    isListFocused(listTagName: string) {
        return !!findClosest(this.focusedNodeElement, listTagName, '.text-editor');
    }

    onGiphyItemClick($event: ITextEditorLinkAttachment) {
        this.toggleGiphyOverlay(false);
        this.onUpdateLinkAttachment.emit($event);
    }

    removeAttachmentFromContent(attachment: ITextEditorLinkAttachment) {
        this.textEditorService.removeAttachmentFromContent(this.elementRef.nativeElement, attachment);
        setTimeout(() => this.updateTextModel());
    }

    onFileDrop(files: FileList) {
        this.UploadFiles(files);
    }

    UploadFiles(files: FileList) {
        this.fileUploadService.silentUploadFiles(files).then((mediaBankFileReferences) => {
            mediaBankFileReferences.forEach((mediaBankReference) => {
                this.onNewAttachment.emit(mediaBankReference);
            });
        });
    }

    insertUL() {
        document.execCommand('insertUnorderedList', false, null);
    }

    insertOL() {
        document.execCommand('insertOrderedList', false, null);
    }

    onSaveClick() {
        this.onSave.emit();
    }

    onCancelClick() {
        if (!this.isContentChanged) {
            this.onCancel.emit();
            return;
        }

        this.fiDialogsService
            .confirm({
                headerText: this.appTextService.get('common.discard_draft_dialog_text'),
                yesButtonText: this.appTextService.get('common.discard'),
            })
            .onYesButtonClick(() => {
                this.textModel = '';
                this.onCancel.emit();
                this.isContentChanged = false;
            });
    }

    updateTextModel() {
        this.ngModel = this.getEditor().innerHTML;
        this.doPropagateChanges();
        this.getEditor().focus();
    }

    changeTextStyles(fontStyleButton, showDefaultUI, valueArgument) {
        super.changeTextStyles(fontStyleButton, showDefaultUI, valueArgument);
        this.getEditor().focus();
    }

    triggerChangeEvent() {
        this.editorElement.nativeElement.dispatchEvent(new Event('change'));
        this.editorElement.nativeElement.focus();
    }

    setFocusedElement() {
        this.focusedNodeElement = this.textEditorService.getFocusedElement();
    }

    protected onNewUserTagged(newTaggedUser: ILightWeightUser) {
        if (this.taggedUsers && !this.taggedUsers.find((u) => u.Id === newTaggedUser.Id)) {
            // add new tagged user to array for proper avatar updating after switching from edit to view mode
            this.taggedUsers.push(newTaggedUser);
        }
    }

    private doPropagateChanges() {
        if (this.controlValueAccessorOnChange) {
            this.controlValueAccessorOnChange(this.ngModel);
            return;
        }
        this.ngModelChange.emit(this.ngModel);
    }

    private getEditor(): HTMLElement {
        return this.elementRef.nativeElement.querySelector('div[contenteditable]');
    }

    /*    private doPropagateChangesDebounced = fiDebounce(() => {
    }, 1000);*/
}
